/* eslint-disable no-nested-ternary */
import React, { forwardRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, Tooltip, Menu, MenuItem } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import { injectIntl, useIntl } from 'react-intl';
import ZoomOutMapIcon from '@material-ui/icons/ZoomOutMap';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import { Checkmark, Printer } from '@mummssoftware/icons';
import { ActionIcon } from '../ActionIcon';
import messages from './messages';
import { WidgetHideConfirmBar } from './WidgetHideConfirmBar';
const WidgetHeaderWrapper = styled.div.withConfig({
  displayName: "WidgetHeader__WidgetHeaderWrapper",
  componentId: "p6plh2-0"
})(["height:", ";background-color:", ";", " letter-spacing:0.0625em;font-weight:normal;font-family:'Muli',sans-serif;border-spacing:0px;display:flex;align-items:center;justify-content:space-between;padding-left:5px;margin-bottom:2px;color:", ";min-width:0;"], props => `${props.theme.props.dashboard.widgetHeaderHeight}px`, props => props.theme.palette.background.paper, props => props.isForm ? `;` : `font-size: 18px;`, props => props.theme.palette.text.primary);
const WidgetTitle = styled.div.withConfig({
  displayName: "WidgetHeader__WidgetTitle",
  componentId: "p6plh2-1"
})(["padding-left:0.5rem;padding-right:0.5rem;", " ", ""], props => props.isForm ? `;` : `text-transform: uppercase;`, props => props.isForm ? props.$expandedFormLayout ? `height: 35px;
      font-size: clamp(0.75rem, 9cqw, 1.125rem);
      display: flex;
      align-items: center;` : `height: 35px;
      font-size: clamp(0.875rem, 3.2vw, 1.125rem);
      display: flex;
      align-items: center;` : `;`);
const WidgetButtonWrapper = styled.div.withConfig({
  displayName: "WidgetHeader__WidgetButtonWrapper",
  componentId: "p6plh2-2"
})(["display:flex;justify-content:flex-end;align-items:center;flex-shrink:0;"]);
const WidgetHeaderIconDivider = styled.div.withConfig({
  displayName: "WidgetHeader__WidgetHeaderIconDivider",
  componentId: "p6plh2-3"
})(["width:1px;align-self:stretch;margin:4px 2px;background-color:", ";"], props => props.theme.palette.divider);
const WidgetLeftWrapper = styled.div.withConfig({
  displayName: "WidgetHeader__WidgetLeftWrapper",
  componentId: "p6plh2-4"
})(["display:flex;justify-content:space-between;align-items:center;flex:1;min-width:0;"]);
/** Header tools (VITALS menu, hide confirm) keep intrinsic width; title yields. */

const WidgetHeaderChildrenSlot = styled.div.withConfig({
  displayName: "WidgetHeader__WidgetHeaderChildrenSlot",
  componentId: "p6plh2-5"
})(["display:flex;align-items:center;flex:0 0 auto;margin-left:4px;"]);
const WidgetNoteWrapper = styled.div.withConfig({
  displayName: "WidgetHeader__WidgetNoteWrapper",
  componentId: "p6plh2-6"
})(["display:flex;gap:5px;align-items:center;flex:1 1 0%;min-width:0;overflow:hidden;"]);
/** Keeps status/note content from shrinking when the title needs the remaining space. */

const WidgetNoteAside = styled.div.withConfig({
  displayName: "WidgetHeader__WidgetNoteAside",
  componentId: "p6plh2-7"
})(["flex-shrink:0;"]);
/**
 * Title area for the left header row: establishes a container so the label font
 * scales down with available width instead of horizontal scrolling.
 */

const WidgetNoteTitleSlot = styled.div.withConfig({
  displayName: "WidgetHeader__WidgetNoteTitleSlot",
  componentId: "p6plh2-8"
})(["flex:", ";min-width:0;overflow:hidden;", ""], p => p.$hugTitle ? '0 1 auto' : '1 1 0%', p => p.$isCqwContainer ? `container-type: inline-size;` : `container-type: normal;`);
const WidgetTitleAction = styled.div.withConfig({
  displayName: "WidgetHeader__WidgetTitleAction",
  componentId: "p6plh2-9"
})(["display:flex;align-items:center;flex-shrink:0;"]);
/**
 * Font size tracks the title slot (container) width when expanded; dashboard tile uses vw. Tuned so
 * ~17px at a 188px-wide title slot: 5.47cqw + 0.42rem with cqw=1% of slot
 * → (17−0.42rem)/1.88px ≈ 5.47.
 */

const WidgetNoteAppTitle = styled(WidgetTitle).withConfig({
  displayName: "WidgetHeader__WidgetNoteAppTitle",
  componentId: "p6plh2-10"
})(["max-width:100%;line-height:1;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;", ""], p => p.$expandedFormLayout ? `font-size: clamp(0.5625rem, 5.47cqw + 0.42rem, 17px);` : `font-size: clamp(0.875rem, 3.2vw, 1.125rem);`);
const ToggleActionButton = withStyles({
  closeIcon: {
    height: '15px'
  },
  closeButton: {
    margin: '0 3px 0 0'
  }
})(injectIntl(({
  showClose,
  toggleWidget,
  link,
  target,
  disabled,
  intl: {
    formatMessage
  },
  classes
}) => link ? /*#__PURE__*/React.createElement(Link, {
  color: "textPrimary",
  style: {
    textDecoration: 'none'
  },
  href: link,
  target: target
}, /*#__PURE__*/React.createElement(ActionIcon, {
  title: showClose ? formatMessage(messages.close) : formatMessage(messages.openOutside),
  Icon: showClose ? Checkmark : OpenInNewIcon,
  onClick: toggleWidget,
  className: classes.closeButton,
  iconProps: {
    className: classes.closeIcon
  }
}), ' ') : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(ActionIcon, {
  title: showClose ? formatMessage(messages.close) : formatMessage(messages.open),
  Icon: showClose ? Checkmark : ZoomOutMapIcon,
  onClick: toggleWidget,
  className: classes.closeButton,
  iconProps: {
    className: classes.closeIcon
  },
  disabled: disabled
}))));

const ToggleWidgetButton = ({
  showClose,
  toggleWidget,
  onlyCloseWidget,
  link,
  disabled,
  target
}) => onlyCloseWidget && !showClose ? null : /*#__PURE__*/React.createElement(ToggleActionButton, {
  toggleWidget: toggleWidget,
  showClose: showClose,
  onlyCloseWidget: onlyCloseWidget,
  link: link,
  target: target,
  disabled: disabled
});

const PrintWidgetButton = withStyles({
  printIcon: {
    height: '15px'
  },
  printButton: {
    margin: '0 3px 0 0'
  }
})(injectIntl(({
  showPrint,
  printWidget,
  disabled,
  intl: {
    formatMessage
  },
  classes
}) => /*#__PURE__*/React.createElement(ActionIcon, {
  title: formatMessage(messages.print),
  Icon: Printer,
  onClick: printWidget,
  className: classes.printButton,
  iconProps: {
    className: classes.printIcon
  },
  visible: showPrint,
  disabled: disabled
})));
const RemoveWidgetButton = withStyles(theme => ({
  removeIcon: {
    height: '15px'
  },
  removeButton: {
    margin: '0 3px 0 0',
    '&:hover $removeIcon': {
      color: theme.palette.error.main
    }
  }
}))(injectIntl(({
  onRemove,
  removeDisabled,
  intl: {
    formatMessage
  },
  classes
}) => /*#__PURE__*/React.createElement(ActionIcon, {
  title: formatMessage(messages.removeWidget),
  Icon: HighlightOffIcon,
  onClick: onRemove,
  className: classes.removeButton,
  disabled: removeDisabled,
  iconProps: {
    className: classes.removeIcon
  }
})));
const FaxWidgetButton = withStyles({
  printIcon: {
    height: '15px'
  },
  printButton: {
    margin: '0 3px 0 0'
  }
})(injectIntl(({
  showFax,
  faxWidget,
  intl: {
    formatMessage
  },
  classes
}) => /*#__PURE__*/React.createElement(ActionIcon, {
  title: formatMessage(messages.fax),
  Icon: Printer,
  onClick: faxWidget,
  className: classes.printButton,
  iconProps: {
    className: classes.printIcon
  },
  visible: !!showFax
})));

var _ref7 = /*#__PURE__*/React.createElement(WidgetHeaderIconDivider, null);

export const WidgetHeader = /*#__PURE__*/forwardRef(({
  onClick,
  onPrint,
  onFax,
  onRemove,
  onEmail,
  showPrint,
  showFax,
  appTitle,
  smallWidget,
  onlyCloseWidget,
  noIcon,
  link,
  target,
  versionInfo,
  disablePrint,
  disabled,
  doNotShow,
  isForm,
  hasNote,
  note,
  titleAction,
  children
}, ref) => {
  var _ref5 = /*#__PURE__*/React.createElement(FaxWidgetButton, {
    faxWidget: onFax,
    showFax: true
  });

  var _ref4 = /*#__PURE__*/React.createElement(PrintWidgetButton, {
    printWidget: onPrint,
    showPrint: true,
    disabled: disablePrint
  });

  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [pendingHide, setPendingHide] = useState(false);
  const [printMenuAnchor, setPrintMenuAnchor] = useState(null);
  const {
    formatMessage
  } = useIntl();
  const hasPrintMenu = showPrint && typeof onPrint === 'function' && (typeof onFax === 'function' || typeof onEmail === 'function');
  /**
   * “Expanded header” / full-size widget: same as `!smallWidget` (1×1 dashboard tile
   * passes `smallWidget: true`). Used for cqw title slot, close checkmark, etc.
   */

  const isExpandedHeader = smallWidget !== true;
  /**
   * cqw + container-type for form-style widgets when the header is expanded.
   * Other widgets (e.g. Measures) use vw-based clamp when expanded.
   */

  const useCqwFormTitle = isExpandedHeader && !!isForm;

  const showClose = !smallWidget,
        _ref6 = /*#__PURE__*/React.createElement(ToggleWidgetButton, {
    toggleWidget: onClick,
    showClose: showClose,
    onlyCloseWidget: onlyCloseWidget,
    link: link,
    target: target,
    disabled: disabled
  });

  const showExpand = !noIcon && !(onlyCloseWidget && !showClose);
  const showRemove = smallWidget === true && typeof onRemove === 'function' && doNotShow !== true;
  const showExpandRemoveDivider = showExpand && showRemove;
  const hasHeaderChildren = Boolean(children);
  const showHideConfirm = pendingHide && showRemove;
  useEffect(() => {
    if (!showRemove) {
      setPendingHide(false);
    }
  }, [showRemove]);

  const handleConfirmHide = () => {
    setPendingHide(false);

    if (typeof onRemove === 'function') {
      onRemove();
    }
  };

  const handleCancelHide = () => {
    setPendingHide(false);
  };

  const handleTitleTooltipClose = () => {
    setTooltipOpen(false);
  };

  const handleTitleTooltipOpen = e => {
    if (e.altKey) {
      setTooltipOpen(true);
    }
  };

  const handlePrintIconClick = e => {
    if (disablePrint) return;

    if (hasPrintMenu) {
      setPrintMenuAnchor(e.currentTarget);
    } else if (typeof onPrint === 'function') {
      onPrint();
    }
  },
        _ref2 = /*#__PURE__*/React.createElement(PrintWidgetButton, {
    printWidget: handlePrintIconClick,
    showPrint: true,
    disabled: disablePrint
  });

  const handlePrintMenuClose = () => setPrintMenuAnchor(null);

  const handlePrint = () => {
    handlePrintMenuClose();
    if (typeof onPrint === 'function') onPrint();
  };

  const handleFax = () => {
    handlePrintMenuClose();
    if (typeof onFax === 'function') onFax();
  };

  const handleRemove = e => {
    e.stopPropagation();
    setPendingHide(true);
  };

  const handleEmail = () => {
    handlePrintMenuClose();
    if (typeof onEmail === 'function') onEmail();
  };
  /**
   * Right-side header actions (print, expand, remove X) or hide confirmation.
   *
   * Hide confirm placement pairs with the left {@code WidgetHeaderChildrenSlot}:
   * - **`children` present** (e.g. Measures VITALS menu): confirm bar replaces `{children}`;
   *   this function returns nothing so expand/print/remove are hidden during confirm.
   * - **No `children`**: confirm bar renders here on the right instead of those icons.
   *
   * Tile view only (`smallWidget`); expanded widgets never show remove or this flow.
   */


  var _ref = /*#__PURE__*/React.createElement(WidgetHideConfirmBar, {
    onConfirm: handleConfirmHide,
    onCancel: handleCancelHide
  });

  var _ref3 = /*#__PURE__*/React.createElement(MenuItem, {
    onClick: handleEmail
  }, "Email");

  const renderHeaderActions = () => {
    if (showHideConfirm) {
      if (!hasHeaderChildren) {
        return _ref;
      }

      return null;
    }

    return /*#__PURE__*/React.createElement(React.Fragment, null, hasPrintMenu ? /*#__PURE__*/React.createElement(React.Fragment, null, _ref2, /*#__PURE__*/React.createElement(Menu, {
      anchorEl: printMenuAnchor,
      open: Boolean(printMenuAnchor),
      onClose: handlePrintMenuClose,
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'center'
      },
      transformOrigin: {
        vertical: 'top',
        horizontal: 'center'
      }
    }, /*#__PURE__*/React.createElement(MenuItem, {
      onClick: handlePrint
    }, formatMessage(messages.print)), typeof onFax === 'function' && /*#__PURE__*/React.createElement(MenuItem, {
      onClick: handleFax
    }, formatMessage(messages.fax)), typeof onEmail === 'function' && _ref3)) : /*#__PURE__*/React.createElement(React.Fragment, null, showPrint && onPrint && _ref4, showFax && onFax && !hasPrintMenu && _ref5), showExpand && _ref6, showExpandRemoveDivider && _ref7, showRemove && /*#__PURE__*/React.createElement(RemoveWidgetButton, {
      onRemove: handleRemove,
      removeDisabled: Boolean(printMenuAnchor)
    }));
  };

  return /*#__PURE__*/React.createElement(WidgetHeaderWrapper, {
    ref: ref,
    className: "widget-header",
    isForm: isForm
  }, /*#__PURE__*/React.createElement(WidgetLeftWrapper, {
    className: "widget-header-left"
  }, /*#__PURE__*/React.createElement(WidgetNoteWrapper, null, hasNote && /*#__PURE__*/React.createElement(WidgetNoteAside, null, note), /*#__PURE__*/React.createElement(WidgetNoteTitleSlot, {
    $isCqwContainer: useCqwFormTitle,
    $hugTitle: Boolean(titleAction)
  }, /*#__PURE__*/React.createElement(Tooltip, {
    placement: "bottom-start",
    title: versionInfo || 'v',
    "aria-label": "about",
    open: tooltipOpen,
    onClose: handleTitleTooltipClose
  }, /*#__PURE__*/React.createElement(WidgetNoteAppTitle, {
    onClick: handleTitleTooltipOpen,
    isForm: isForm,
    $expandedFormLayout: useCqwFormTitle
  }, appTitle))), titleAction && /*#__PURE__*/React.createElement(WidgetTitleAction, null, titleAction)), hasHeaderChildren && /*#__PURE__*/React.createElement(WidgetHeaderChildrenSlot, {
    className: "widget-header-children"
  }, showHideConfirm ? /*#__PURE__*/React.createElement(WidgetHideConfirmBar, {
    onConfirm: handleConfirmHide,
    onCancel: handleCancelHide
  }) : children)), /*#__PURE__*/React.createElement(WidgetButtonWrapper, {
    className: "widget-button"
  }, renderHeaderActions()));
});
export default WidgetHeader;